import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeDetails,
  getCurrentPage,
  openDetails,
  toggleModal,
} from 'reducers/ui';
import BaseDetails from 'containers/Base/BaseDetails';
import { getZoneDetailsTransformed } from '../Selectors/zoneSelectors';
import { actions as zoneActions } from '../Modules/dataGridTable';
import staticText from 'static/text_display.json';
import {
  translatedKeyValueText,
  languageConverter,
} from 'utils/methods/helpers';
import { nameSpace } from '../Modules/nameSpace';

const ZoneDetails = ({ editable, toggleEditMode, id, cancel, loading }) => {
  //------------------------------Selectors---------------------------------
  const details = useSelector(state => getZoneDetailsTransformed(state));
  const pageUrl = useSelector(state => getCurrentPage(state));
  //------------------------------------------------------------------------
  //-----------------------Actions------------------------------------------
  const dispatch = useDispatch();

  const dispatchFetchZoneDetails = useCallback(
    id => dispatch(zoneActions.fetchGridRowDetails(id)),
    [dispatch],
  );
  const dispatchToggleModal = useCallback(
    nameSpace => dispatch(toggleModal(nameSpace)),
    [dispatch],
  );
  const dispatchOpenDetails = useCallback(url => dispatch(openDetails(url)), [
    dispatch,
  ]);
  const dispatchSetZoneID = useCallback(
    id => {
      dispatch(zoneActions.setSelectedRowID(id));
    },
    [dispatch],
  );
  const dispatchClearZoneDetails = useCallback(
    () => dispatch(zoneActions.setGridRowDetails({})),
    [dispatch],
  );
  const dispatchRemoveZoneOnMapId = useCallback(
    () => dispatch(zoneActions.setSelectedRowID(null)),
    [dispatch],
  );

  const dispatchCloseDetails = useCallback(
    url => {
      dispatch(closeDetails(url));
    },
    [dispatch],
  );

  //----------------------------------------------------------------------------
  useEffect(() => {
    if (id) {
      dispatchFetchZoneDetails(id); // fetch the details
      dispatchOpenDetails(pageUrl); // track the detail panel state
      dispatchSetZoneID(id); // set the zone ID
    }
    // eslint-disable-next-line no-empty-function
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onEdit = () => {
    toggleEditMode();
    dispatchToggleModal(nameSpace);
  };

  //attributes that needs to be system language translated
  const languageConvertedZoneDetail = details => {
    return languageConverter({
      details,
      arrayOfKeys: ['riskLevel', 'consequence', 'likelihood'],
    });
  };
  const cancelDetails = () => {
    cancel();
    dispatchCloseDetails(pageUrl);
    dispatchClearZoneDetails();
    dispatchRemoveZoneOnMapId();
  };

  return (
    <BaseDetails
      editable={editable}
      header={staticText.managerial_view.zone.details}
      details={languageConvertedZoneDetail(details)}
      onEdit={onEdit}
      cancel={cancelDetails}
      loading={loading}
      editHoverText={translatedKeyValueText('edit_value_title', {
        value:
          Object.keys(details).length > 0
            ? details.generalInfo.name.value
            : staticText.managerial_view.zone.edit_default,
      })}
    />
  );
};

ZoneDetails.propTypes = {
  id: PropTypes.string.isRequired,
  cancel: PropTypes.func.isRequired,
  toggleEditMode: PropTypes.func,
  editable: PropTypes.bool,
  loading: PropTypes.bool,
};

export default ZoneDetails;
